import { useCallback, useEffect, useState } from "react";
import { Body2, focusVisibleRingStyle, ResponsiveImage, Subtitle2, useInteractiveElement, XStack, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils";
import type { ReferralInviteTrigger } from "../../lib/analytics";
import { useBoolVariation } from "../../lib/flag";
import { CARD_FAN } from "./images";
import { ReferralSheet } from "./ReferralSheet";
export type ReferralBannerProps = {
  autoOpen: boolean;
  onAutoOpened: () => void;
};
export function ReferralBanner({
  autoOpen,
  onAutoOpened
}: ReferralBannerProps) {
  const enabled = useBoolVariation(Flag.REFERRAL_PROGRAM, false);
  const [openedBy, setOpenedBy] = useState<Exclude<ReferralInviteTrigger, "settings"> | null>(null);
  useEffect(() => {
    if (autoOpen) {
      // A sheet the user already opened wins; that session's Welcome is dropped, not deferred.
      setOpenedBy(current => current ?? "welcome");
      onAutoOpened();
    }
  }, [autoOpen, onAutoOpened]);
  const openSheet = useCallback(() => setOpenedBy("banner"), []);
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setOpenedBy(null);
    }
  }, []);
  const {
    interactiveProps,
    renderTag
  } = useInteractiveElement({
    kind: "button",
    onPress: openSheet
  });
  if (!enabled) {
    return null;
  }
  return <>
      <XStack testID="home.referral-banner" alignSelf="flex-start" alignItems="center" gap="$sp1" data-sentry-element="XStack" data-sentry-source-file="ReferralBanner.tsx">
        <XStack {...interactiveProps} render={renderTag} group
      // `group` makes this a size container, which collapses a row sized by its content.
      containerType="normal" flexShrink={1} alignItems="center" gap="$sp1.5" paddingVertical="$sp0.5" cursor="pointer" focusVisibleStyle={focusVisibleRingStyle} data-sentry-element="XStack" data-sentry-source-file="ReferralBanner.tsx">
          <YStack width="$si8" data-sentry-element="YStack" data-sentry-source-file="ReferralBanner.tsx">
            <ResponsiveImage src={CARD_FAN} alt="" data-sentry-element="ResponsiveImage" data-sentry-source-file="ReferralBanner.tsx" />
          </YStack>
          <XStack flexShrink={1} flexWrap="wrap" columnGap="$sp1" data-sentry-element="XStack" data-sentry-source-file="ReferralBanner.tsx">
            <Body2 color="$textDefault" data-sentry-element="Body2" data-sentry-source-file="ReferralBanner.tsx">
              Get $20 by referring a friend to Granted!
            </Body2>
            <Subtitle2 color="$textDefault" $group-hover={{
            textDecorationLine: "underline"
          }} data-sentry-element="Subtitle2" data-sentry-source-file="ReferralBanner.tsx">
              Learn more
            </Subtitle2>
          </XStack>
        </XStack>
      </XStack>
      <ReferralSheet open={openedBy !== null} onOpenChange={handleOpenChange} content={openedBy === "welcome" ? "welcome" : "share"} trigger={openedBy ?? "banner"} data-sentry-element="ReferralSheet" data-sentry-source-file="ReferralBanner.tsx" />
    </>;
}